"use client";

import { useState } from "react";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import type { UniversalEnhancements } from "@/lib/conditions/unified-types";
import { resolveSlotSport } from "@/lib/conditions/sport-aware-slot";
import type { LuckFactor } from "@/lib/conditions/luck";
import type { MetricCell } from "@/lib/conditions/metric-strip";
import type { ScoreExplanation } from "@/lib/conditions/factor-explanations";
import { sportLabel } from "@/lib/conditions/sport-meta";
import { scoreBandTextClass } from "@/lib/conditions/score-colour";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { formatLuckDelta, luckDeltaColour, shouldShowLuckChip } from "@/lib/conditions/luck-chip";
import { MetricStrip } from "./MetricStrip";
import { TrendBadge } from "./TrendBadge";
import { RarityBadge } from "./RarityBadge";
import { LuckDetail } from "./LuckDetail";
import { FactorHeadlineSummary, type ActiveFitPresentation } from "./insights/FactorFirst";

export type VerdictSportData = {
  label: string | null;
  score: number | null;
  note: string | null;
  metrics: MetricCell[];
  tacticalLine: string | null;
  explanation?: ScoreExplanation | null;
  activeFit?: ActiveFitPresentation | null;
  unavailableReason?: string;
  /**
   * Set when the score is the previous run's forecast for today (the nightly gap,
   * lib/conditions/gap-forecast.ts), for example "Forecast issued 21:56 CEST": it replaces
   * "Today's score" in the heading, so the number never reads as today's score.
   */
  forecastHeading?: string;
  /** Why this venue is not ranked for the sport (curated eligibility); shown under the score. */
  eligibilityNotice?: string;
  /**
   * Server-rendered evidence line (the confidence badge and the optional-input note,
   * app/conditions/sport-evidence.tsx); absent unless the page has one.
   */
  evidenceSlot?: React.ReactNode;
};

export function VerdictBlock({
  defaultSport,
  perSport,
  enhancements,
  perSportLuck,
  availableSports,
  headerActions,
}: {
  defaultSport: string;
  perSport: Record<string, VerdictSportData>;
  enhancements: Record<string, UniversalEnhancements>;
  perSportLuck: Record<string, LuckFactor | null>;
  availableSports?: readonly string[];
  headerActions?: React.ReactNode;
}) {
  const urlSport = useShallowSearchParam("sport") ?? defaultSport;
  const activeSport = resolveSlotSport(
    urlSport,
    defaultSport,
    availableSports ?? Object.keys(perSport),
  );
  const data = perSport[activeSport];
  const enh = enhancements[activeSport] ?? null;
  const luck = perSportLuck[activeSport] ?? null;
  const showLuck = shouldShowLuckChip(luck);
  const [luckOpen, setLuckOpen] = useState(false);

  if (!data || data.score == null) {
    return (
      <section className="relative">
        <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          {headerActions && <div className="mb-2 flex justify-end">{headerActions}</div>}
          <p className="text-[14px] text-text-muted">
            {data?.unavailableReason ?? `${sportLabel(activeSport)} scoring is currently unavailable.`}
          </p>
          {data?.metrics && <MetricStrip cells={data.metrics} />}
        </div>
      </section>
    );
  }

  // Band-coloured headline number, readable on the white card. NOT
  // scoreBadgeColor().text, which is always white (meant for a coloured fill).
  const scoreText = scoreBandTextClass(data.score);

  return (
    <section className="relative">
      <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        {headerActions && <div className="float-right ml-2">{headerActions}</div>}
        <div className="text-[11px] font-bold uppercase tracking-[1px] text-gold">
          {sportLabel(activeSport)} · {data.forecastHeading ?? "Today’s score"} · {data.label ?? "Label unavailable"}
        </div>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className={`font-serif text-[40px] font-bold tabular-nums leading-none ${scoreText}`}>
            {scoreOutOf100(data.score)}
          </span>
          <span className="text-[13px] text-text-muted">/100</span>
        </div>
        {data.note && <p className="text-[14px] leading-[1.5] text-text-body mt-2">{data.note}</p>}
        {data.eligibilityNotice && (
          <p role="note" className="mt-2 text-[13px] leading-[1.45] text-text-muted">{data.eligibilityNotice}</p>
        )}
        {data.evidenceSlot && <div className="mt-2">{data.evidenceSlot}</div>}

        {data.explanation && (
          <div className="mt-4">
            <FactorHeadlineSummary explanation={data.explanation} activeFit={data.activeFit} />
          </div>
        )}

        <MetricStrip cells={data.metrics} />


        {data.tacticalLine && (
          <p className="mt-2 text-[12.5px] leading-[1.45] text-navy bg-cream border-l-[3px] border-gold rounded-r-[8px] px-2.5 py-1.5">
            {data.tacticalLine}
          </p>
        )}

        {(enh && (enh.trend || enh.rarity)) || showLuck ? (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {enh?.trend && <TrendBadge trend={enh.trend} />}
            {enh?.rarity && <RarityBadge rarity={enh.rarity} />}
            {showLuck && luck && (() => {
              const { label } = formatLuckDelta(luck.delta);
              return (
                <button
                  type="button"
                  onClick={() => setLuckOpen((v) => !v)}
                  aria-expanded={luckOpen}
                  aria-controls="verdict-luck-detail"
                  className="inline-flex min-h-11 min-w-11 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
                  style={{ backgroundColor: luckDeltaColour(luck.delta) }}
                >
                  <span aria-hidden="true">✦</span> Luck {label}
                </button>
              );
            })()}
          </div>
        ) : null}

        {showLuck && luck && (
          <div
            id="verdict-luck-detail"
            hidden={!luckOpen}
            className="mt-3 rounded-[12px] border border-border-light bg-warm-bg p-3.5"
          >
            <LuckDetail luck={luck} />
          </div>
        )}
      </div>
    </section>
  );
}
